import { useState } from "react";
import {
    Button,
    ButtonColor,
    Container,
    ContainerSize,
    Gap,
    Heading,
    Offcanvas,
    OffcanvasPlacement,
    OffcanvasResponsive,
    Stack,
    StackItem,
    Text,
} from "@rebootui";
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";

const OffcanvasPage = () => {
    // Single state to manage all offcanvas instances
    const [activeOffcanvas, setActiveOffcanvas] = useState<string | null>(null);

    // Generic handlers
    const openOffcanvas = (offcanvasName: string) => setActiveOffcanvas(offcanvasName);
    const closeOffcanvas = () => setActiveOffcanvas(null);

    // Check if specific offcanvas is open
    const isOffcanvasOpen = (offcanvasName: string) => activeOffcanvas === offcanvasName;

    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Offcanvas
            </Heading>

            <Text className="mb-4">
                Slide-in panel from start, end, top, or bottom. Control it with <Text el="strong">show</Text> and{" "}
                <Text el="strong">onClose</Text>; backdrop click and <Text el="kbd">Escape</Text> close by default unless you
                disable backdrop dismissal.
            </Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Usage
            </Heading>
            <Text className="mb-4">
                Open from a button or link; close with the header control, backdrop, or <Text el="kbd">Escape</Text>.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <StackItem>
                            <Button color={ButtonColor.Primary} onClick={() => openOffcanvas('basic')}>Offcanvas with
                                button</Button>
                            <Offcanvas show={isOffcanvasOpen('basic')} onClose={closeOffcanvas}>
                                <Offcanvas.Header>
                                    <Heading el={'h5'} className="offcanvas-title">Offcanvas Title</Heading>
                                    <Button type="button" className="btn-close"
                                        onClick={closeOffcanvas}>&nbsp;</Button>
                                </Offcanvas.Header>
                                <Offcanvas.Body>
                                    <Text el="p">
                                        Learn How to Quickly Generate Placeholder Text Using a Lorem Ipsum Tool. Explore
                                        How Lorem Ipsum Generators Can Liven up Your Web Layout Wireframes and Mockups.
                                        100+ Templates. A/B Testing & Surveys. Insights & Analytics.
                                    </Text>
                                </Offcanvas.Body>
                            </Offcanvas>
                        </StackItem>
                        <StackItem>
                            <Button el="a" href="#" color={ButtonColor.Primary}
                                onClick={() => openOffcanvas('link')}>Offcanvas with link</Button>
                            <Offcanvas show={isOffcanvasOpen('link')} onClose={closeOffcanvas}>
                                <Offcanvas.Header>
                                    <Heading el={'h5'} className="offcanvas-title">Offcanvas Title</Heading>
                                    <Button type="button" className="btn-close"
                                        onClick={closeOffcanvas}>&nbsp;</Button>
                                </Offcanvas.Header>
                                <Offcanvas.Body>
                                    <Text el="p">
                                        Learn How to Quickly Generate Placeholder Text Using a Lorem Ipsum Tool. Explore
                                        How Lorem Ipsum Generators Can Liven up Your Web Layout Wireframes and Mockups.
                                        100+ Templates. A/B Testing & Surveys. Insights & Analytics.
                                    </Text>
                                </Offcanvas.Body>
                            </Offcanvas>
                        </StackItem>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {OffcanvasBasicCode}
                </CodeHighlighter>
                <CodeHighlighter language="tsx">
                    {OffcanvasHandlersCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Body scrolling
            </Heading>
            <Text className="mb-4">
                Set <Text el="strong">bodyScroll=true</Text> to keep the page scrollable while the panel is open (no backdrop is
                shown).
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Button color={ButtonColor.Primary} onClick={() => openOffcanvas('scroll')}>Offcanvas body
                        scroll</Button>
                    <Offcanvas show={isOffcanvasOpen('scroll')} onClose={closeOffcanvas} bodyScroll={true}>
                        <Offcanvas.Header>
                            <Heading el={'h5'} className="offcanvas-title">Offcanvas Title</Heading>
                            <Button type="button" className="btn-close"
                                onClick={closeOffcanvas}>&nbsp;</Button>
                        </Offcanvas.Header>
                        <Offcanvas.Body>
                            <Text el="p">
                                Learn How to Quickly Generate Placeholder Text Using a Lorem Ipsum Tool. Explore
                                How Lorem Ipsum Generators Can Liven up Your Web Layout Wireframes and Mockups.
                                100+ Templates. A/B Testing & Surveys. Insights & Analytics.
                            </Text>
                        </Offcanvas.Body>
                    </Offcanvas>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {OffcanvasScrollCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Static backdrop
            </Heading>
            <Text className="mb-4">
                With <Text el="strong">backdropClose=&#123;false&#125;</Text>, clicks on the backdrop do not close the panel;
                use the close control or <Text el="kbd">Escape</Text>.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Button color={ButtonColor.Primary} onClick={() => openOffcanvas('backdrop')}>Static backdrop
                    </Button>
                    <Offcanvas show={isOffcanvasOpen('backdrop')} onClose={closeOffcanvas}
                        backdropClose={false}>
                        <Offcanvas.Header>
                            <Heading el={'h5'} className="offcanvas-title">Offcanvas Title</Heading>
                            <Button type="button" className="btn-close"
                                onClick={closeOffcanvas}>&nbsp;</Button>
                        </Offcanvas.Header>
                        <Offcanvas.Body>
                            <Text el="p">
                                Learn How to Quickly Generate Placeholder Text Using a Lorem Ipsum Tool. Explore
                                How Lorem Ipsum Generators Can Liven up Your Web Layout Wireframes and Mockups.
                                100+ Templates. A/B Testing & Surveys. Insights & Analytics.
                            </Text>
                        </Offcanvas.Body>
                    </Offcanvas>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {OffcanvasBackdropCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Responsive
            </Heading>
            <Text className="mb-4">
                Use <Text el="strong">responsive=&#123;OffcanvasResponsive.*&#125;</Text> so the sliding panel only applies
                below a breakpoint (same idea as Bootstrap responsive offcanvas).
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Text className="my-2 d-none d-lg-block"><Text el="strong">Note - Resize your browser to show the
                        responsive offcanvas toggle.</Text></Text>
                    <Button color={ButtonColor.Primary} className="d-lg-none"
                        onClick={() => openOffcanvas('responsive')}>Responsive</Button>
                    <Offcanvas show={isOffcanvasOpen('responsive')} onClose={closeOffcanvas}
                        responsive={OffcanvasResponsive.Lg}>
                        <Offcanvas.Header>
                            <Heading el={'h5'} className="offcanvas-title">Offcanvas Title</Heading>
                            <Button type="button" className="btn-close"
                                onClick={closeOffcanvas}>&nbsp;</Button>
                        </Offcanvas.Header>
                        <Offcanvas.Body>
                            <Text el="p">
                                Learn How to Quickly Generate Placeholder Text Using a Lorem Ipsum Tool. Explore
                                How Lorem Ipsum Generators Can Liven up Your Web Layout Wireframes and Mockups.
                                100+ Templates. A/B Testing & Surveys. Insights & Analytics.
                            </Text>
                        </Offcanvas.Body>
                    </Offcanvas>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {OffcanvasResponsiveCode}
                </CodeHighlighter>
            </CodeSnippet>
            <Text className="mb-4">
                With <Text el="strong">OffcanvasResponsive.Lg</Text>, the offcanvas behavior applies below the large
                breakpoint; resize the window to try it.
            </Text>

            <Text className="mb-4">
                Available values: <Text el="strong">Sm</Text>, <Text el="strong">Md</Text>, <Text el="strong">Lg</Text>,{" "}
                <Text el="strong">Xl</Text>, <Text el="strong">Xxl</Text> — each maps to a Bootstrap{" "}
                <Text el="strong">offcanvas-*</Text> width class.
            </Text>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Placement
            </Heading>
            <Text className="mb-4">
                Default <Text el="strong">OffcanvasPlacement.Start</Text>; use <Text el="strong">End</Text>,{" "}
                <Text el="strong">Top</Text>, or <Text el="strong">Bottom</Text> for other edges.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Button color={ButtonColor.Primary} onClick={() => openOffcanvas('start')}>Toggle left
                        offcanvas</Button>
                    <Offcanvas show={isOffcanvasOpen('start')} onClose={closeOffcanvas}
                        placement={OffcanvasPlacement.Start}>
                        <Offcanvas.Header>
                            <Heading el={'h5'} className="offcanvas-title">Offcanvas Title</Heading>
                            <Button type="button" className="btn-close"
                                onClick={closeOffcanvas}>&nbsp;</Button>
                        </Offcanvas.Header>
                        <Offcanvas.Body>
                            <Text el="p">
                                Learn How to Quickly Generate Placeholder Text Using a Lorem Ipsum Tool. Explore
                                How Lorem Ipsum Generators Can Liven up Your Web Layout Wireframes and Mockups.
                                100+ Templates. A/B Testing & Surveys. Insights & Analytics.
                            </Text>
                        </Offcanvas.Body>
                    </Offcanvas>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {OffcanvasStartCode}
                </CodeHighlighter>
            </CodeSnippet>

            <CodeSnippet>
                <CodePreview>
                    <Button color={ButtonColor.Primary} onClick={() => openOffcanvas('end')}>Toggle right
                        offcanvas</Button>
                    <Offcanvas show={isOffcanvasOpen('end')} onClose={closeOffcanvas}
                        placement={OffcanvasPlacement.End}>
                        <Offcanvas.Header>
                            <Heading el={'h5'} className="offcanvas-title">Offcanvas Title</Heading>
                            <Button type="button" className="btn-close"
                                onClick={closeOffcanvas}>&nbsp;</Button>
                        </Offcanvas.Header>
                        <Offcanvas.Body>
                            <Text el="p">
                                Learn How to Quickly Generate Placeholder Text Using a Lorem Ipsum Tool. Explore
                                How Lorem Ipsum Generators Can Liven up Your Web Layout Wireframes and Mockups.
                                100+ Templates. A/B Testing & Surveys. Insights & Analytics.
                            </Text>
                        </Offcanvas.Body>
                    </Offcanvas>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {OffcanvasEndCode}
                </CodeHighlighter>
            </CodeSnippet>

            <CodeSnippet>
                <CodePreview>
                    <Button color={ButtonColor.Primary} onClick={() => openOffcanvas('top')}>Toggle top offcanvas</Button>
                    <Offcanvas show={isOffcanvasOpen('top')} onClose={closeOffcanvas}
                        placement={OffcanvasPlacement.Top}>
                        <Offcanvas.Header>
                            <Heading el={'h5'} className="offcanvas-title">Offcanvas Title</Heading>
                            <Button type="button" className="btn-close"
                                onClick={closeOffcanvas}>&nbsp;</Button>
                        </Offcanvas.Header>
                        <Offcanvas.Body>
                            <Text el="p">
                                Learn How to Quickly Generate Placeholder Text Using a Lorem Ipsum Tool. Explore
                                How Lorem Ipsum Generators Can Liven up Your Web Layout Wireframes and Mockups.
                                100+ Templates. A/B Testing & Surveys. Insights & Analytics.
                            </Text>
                        </Offcanvas.Body>
                    </Offcanvas>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {OffcanvasTopCode}
                </CodeHighlighter>
            </CodeSnippet>

            <CodeSnippet>
                <CodePreview>
                    <Button color={ButtonColor.Primary} onClick={() => openOffcanvas('bottom')}>Toggle bottom
                        offcanvas</Button>
                    <Offcanvas show={isOffcanvasOpen('bottom')} onClose={closeOffcanvas}
                        placement={OffcanvasPlacement.Bottom}>
                        <Offcanvas.Header>
                            <Heading el={'h5'} className="offcanvas-title">Offcanvas Title</Heading>
                            <Button type="button" className="btn-close"
                                onClick={closeOffcanvas}>&nbsp;</Button>
                        </Offcanvas.Header>
                        <Offcanvas.Body>
                            <Text el="p">
                                Learn How to Quickly Generate Placeholder Text Using a Lorem Ipsum Tool. Explore
                                How Lorem Ipsum Generators Can Liven up Your Web Layout Wireframes and Mockups.
                                100+ Templates. A/B Testing & Surveys. Insights & Analytics.
                            </Text>
                        </Offcanvas.Body>
                    </Offcanvas>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {OffcanvasBottomCode}
                </CodeHighlighter>
            </CodeSnippet>

        </Container>
    )
}

export default OffcanvasPage;

const OffcanvasBasicCode = `<Stack gap={Gap.Gap2}>
    <StackItem>
        <Button color={ButtonColor.Primary} onClick={showOffcanvasHandler}>Offcanvas with
            button</Button>
        <Offcanvas show={offcanvasShow} onClose={hideOffcanvasHandler}>
            <Offcanvas.Header>
                <Heading el={'h5'} className="offcanvas-title">Offcanvas Title</Heading>
                <Button type="button" className="btn-close"
                        onClick={hideOffcanvasHandler}>&nbsp;</Button>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <Text el="p">
                    Learn How to Quickly Generate Placeholder Text Using a Lorem Ipsum Tool. Explore
                    How Lorem Ipsum Generators Can Liven up Your Web Layout Wireframes and Mockups.
                    100+ Templates. A/B Testing & Surveys. Insights & Analytics.
                </Text>
            </Offcanvas.Body>
        </Offcanvas>
    </StackItem>
    <StackItem>
        <Button el="a" href="#" color={ButtonColor.Primary}
                onClick={showOffcanvasLinkHandler}>Offcanvas with link</Button>
        <Offcanvas show={offcanvasLinkShow} onClose={hideOffcanvasLinkHandler}>
            <Offcanvas.Header>
                <Heading el={'h5'} className="offcanvas-title">Offcanvas Title</Heading>
                <Button type="button" className="btn-close"
                        onClick={hideOffcanvasLinkHandler}>&nbsp;</Button>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <Text el="p">
                    Learn How to Quickly Generate Placeholder Text Using a Lorem Ipsum Tool. Explore
                    How Lorem Ipsum Generators Can Liven up Your Web Layout Wireframes and Mockups.
                    100+ Templates. A/B Testing & Surveys. Insights & Analytics.
                </Text>
            </Offcanvas.Body>
        </Offcanvas>
    </StackItem>
</Stack>`;

const OffcanvasHandlersCode = `// State management for offcanvas
const [offcanvasShow, setOffcanvasShow] = React.useState(false);

// Handler to show offcanvas
const showOffcanvasHandler = () => {
    setOffcanvasShow(true);
};

// Handler to hide offcanvas
const hideOffcanvasHandler = () => {
    setOffcanvasShow(false);
};

// Handler with custom function on open
const showOffcanvasWithFunction = () => {
    console.log('Executing custom logic before opening...');
    // Your custom logic here (load data, validation, etc.)
    setOffcanvasShow(true);
};

// Handler with custom function on close
const hideOffcanvasWithFunction = () => {
    console.log('Executing custom logic before closing...');
    // Your custom logic here (save state, cleanup, etc.)
    setOffcanvasShow(false);
};
`;

const OffcanvasScrollCode = `<Button color={ButtonColor.Primary} onClick={showOffcanvasScrollHandler}>Offcanvas body scroll</Button>
<Offcanvas show={offcanvasScrollShow} onClose={hideOffcanvasScrollHandler} bodyScroll={true}>
    <Offcanvas.Header>
        <Heading el={'h5'} className="offcanvas-title">Offcanvas Title</Heading>
        <Button type="button" className="btn-close"
                onClick={hideOffcanvasScrollHandler}>&nbsp;</Button>
    </Offcanvas.Header>
    <Offcanvas.Body>
        <Text el="p">
            Learn How to Quickly Generate Placeholder Text Using a Lorem Ipsum Tool. Explore
            How Lorem Ipsum Generators Can Liven up Your Web Layout Wireframes and Mockups.
            100+ Templates. A/B Testing & Surveys. Insights & Analytics.
        </Text>
    </Offcanvas.Body>
</Offcanvas>`;

const OffcanvasBackdropCode = `<Button color={ButtonColor.Primary} onClick={showOffcanvasBackdropHandler}>Static backdrop</Button>
<Offcanvas show={offcanvasBackdropShow} onClose={hideOffcanvasBackdropHandler} backdropClose={false}>
    <Offcanvas.Header>
        <Heading el={'h5'} className="offcanvas-title">Offcanvas Title</Heading>
        <Button type="button" className="btn-close"
                onClick={hideOffcanvasBackdropHandler}>&nbsp;</Button>
    </Offcanvas.Header>
    <Offcanvas.Body>
        <Text el="p">
            Learn How to Quickly Generate Placeholder Text Using a Lorem Ipsum Tool. Explore
            How Lorem Ipsum Generators Can Liven up Your Web Layout Wireframes and Mockups.
            100+ Templates. A/B Testing & Surveys. Insights & Analytics.
        </Text>
    </Offcanvas.Body>
</Offcanvas>`;

const OffcanvasResponsiveCode = `<Button color={ButtonColor.Primary} className="d-lg-none" onClick={showOffcanvasResponsiveHandler}>Responsive</Button>
<Offcanvas show={offcanvasResponsiveShow} onClose={hideOffcanvasResponsiveHandler} responsive={OffcanvasResponsive.Lg}>
    <Offcanvas.Header>
        <Heading el={'h5'} className="offcanvas-title">Offcanvas Title</Heading>
        <Button type="button" className="btn-close"
                onClick={hideOffcanvasResponsiveHandler}>&nbsp;</Button>
    </Offcanvas.Header>
    <Offcanvas.Body>
        <Text el="p">
            Learn How to Quickly Generate Placeholder Text Using a Lorem Ipsum Tool. Explore
            How Lorem Ipsum Generators Can Liven up Your Web Layout Wireframes and Mockups.
            100+ Templates. A/B Testing & Surveys. Insights & Analytics.
        </Text>
    </Offcanvas.Body>
</Offcanvas>`;

const OffcanvasStartCode = `<Button color={ButtonColor.Primary} onClick={showOffcanvasStartHandler}>Toggle left offcanvas</Button>
<Offcanvas show={offcanvasStartShow} onClose={hideOffcanvasStartHandler} placement={OffcanvasPlacement.Start}>
    <Offcanvas.Header>
        <Heading el={'h5'} className="offcanvas-title">Offcanvas Title</Heading>
        <Button type="button" className="btn-close"
                onClick={hideOffcanvasStartHandler}>&nbsp;</Button>
    </Offcanvas.Header>
    <Offcanvas.Body>
        <Text el="p">
            Learn How to Quickly Generate Placeholder Text Using a Lorem Ipsum Tool. Explore
            How Lorem Ipsum Generators Can Liven up Your Web Layout Wireframes and Mockups.
            100+ Templates. A/B Testing & Surveys. Insights & Analytics.
        </Text>
    </Offcanvas.Body>
</Offcanvas>`;

const OffcanvasEndCode = `<Button color={ButtonColor.Primary} onClick={showOffcanvasEndHandler}>Toggle right offcanvas</Button>
<Offcanvas show={offcanvasEndShow} onClose={hideOffcanvasEndHandler} placement={OffcanvasPlacement.End}>
    <Offcanvas.Header>
        <Heading el={'h5'} className="offcanvas-title">Offcanvas Title</Heading>
        <Button type="button" className="btn-close"
                onClick={hideOffcanvasEndHandler}>&nbsp;</Button>
    </Offcanvas.Header>
    <Offcanvas.Body>
        <Text el="p">
            Learn How to Quickly Generate Placeholder Text Using a Lorem Ipsum Tool. Explore
            How Lorem Ipsum Generators Can Liven up Your Web Layout Wireframes and Mockups.
            100+ Templates. A/B Testing & Surveys. Insights & Analytics.
        </Text>
    </Offcanvas.Body>
</Offcanvas>`;

const OffcanvasTopCode = `<Button color={ButtonColor.Primary} onClick={showOffcanvasTopHandler}>Toggle top offcanvas</Button>
<Offcanvas show={offcanvasTopShow} onClose={hideOffcanvasTopHandler} placement={OffcanvasPlacement.Top}>
    <Offcanvas.Header>
        <Heading el={'h5'} className="offcanvas-title">Offcanvas Title</Heading>
        <Button type="button" className="btn-close"
                onClick={hideOffcanvasTopHandler}>&nbsp;</Button>
    </Offcanvas.Header>
    <Offcanvas.Body>
        <Text el="p">
            Learn How to Quickly Generate Placeholder Text Using a Lorem Ipsum Tool. Explore
            How Lorem Ipsum Generators Can Liven up Your Web Layout Wireframes and Mockups.
            100+ Templates. A/B Testing & Surveys. Insights & Analytics.
        </Text>
    </Offcanvas.Body>
</Offcanvas>`;

const OffcanvasBottomCode = `<Button color={ButtonColor.Primary} onClick={showOffcanvasBottomHandler}>Toggle bottom offcanvas</Button>
<Offcanvas show={offcanvasBottomShow} onClose={hideOffcanvasBottomHandler} placement={OffcanvasPlacement.Bottom}>
    <Offcanvas.Header>
        <Heading el={'h5'} className="offcanvas-title">Offcanvas Title</Heading>
        <Button type="button" className="btn-close"
                onClick={hideOffcanvasBottomHandler}>&nbsp;</Button>
    </Offcanvas.Header>
    <Offcanvas.Body>
        <Text el="p">
            Learn How to Quickly Generate Placeholder Text Using a Lorem Ipsum Tool. Explore
            How Lorem Ipsum Generators Can Liven up Your Web Layout Wireframes and Mockups.
            100+ Templates. A/B Testing & Surveys. Insights & Analytics.
        </Text>
    </Offcanvas.Body>
</Offcanvas>`;

